import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { authClient } from "../../lib/auth-client";
import { api, ApiRequestError } from "../../lib/api";
import { useSignedInContext } from "../auth/useSignedInContext";

function PodcastFeedSection() {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get<{ hasAccess: boolean }>("/api/v1/podcast/access")
      .then((r) => setHasAccess(r.hasAccess))
      .catch(() => setError("Status konnte nicht geladen werden."));
  }, []);

  async function onCreateOrReissue() {
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.post<{ feedUrl: string }>("/api/v1/podcast/access");
      setFeedUrl(result.feedUrl);
      setHasAccess(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Link konnte nicht erzeugt werden.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="pw-card">
      <h2 style={{ margin: "0 0 0.4rem", fontSize: "1.05rem", fontWeight: 600 }}>Persönlicher Podcast-Feed</h2>
      <p style={{ margin: "0 0 0.8rem", fontSize: "0.85rem", color: "var(--pw-color-text-muted)" }}>
        Der Link ist an euer Konto gebunden und funktioniert in jeder Podcast-App (z. B. Apple Podcasts, private RSS-Abo). Ein neuer Link widerruft den alten sofort.
      </p>
      {error && <div className="pw-alert pw-alert--error">{error}</div>}
      {feedUrl && (
        <div className="pw-alert pw-alert--info">
          Neuer Feed-Link (wird nur jetzt angezeigt):
          <br />
          <code style={{ wordBreak: "break-all", fontFamily: "var(--pw-font-mono)" }}>{feedUrl}</code>
        </div>
      )}
      {hasAccess !== null && (
        <button type="button" className="pw-button pw-button--primary" onClick={() => void onCreateOrReissue()} disabled={submitting}>
          {submitting ? "Wird erzeugt …" : hasAccess ? "Link widerrufen & neu erzeugen" : "Feed-Link erzeugen"}
        </button>
      )}
    </section>
  );
}

export function AccountPage() {
  const { email, member } = useSignedInContext();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState<{ kind: "error" | "info"; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    setSubmitting(true);
    const { error } = await authClient.changePassword({ currentPassword, newPassword, revokeOtherSessions: true });
    setSubmitting(false);
    if (error) {
      setMessage({ kind: "error", text: "Aktuelles Passwort ist falsch, oder neues Passwort ungültig." });
      return;
    }
    setCurrentPassword("");
    setNewPassword("");
    setMessage({ kind: "info", text: "Passwort geändert. Andere Sitzungen wurden abgemeldet." });
  }

  async function onLogout() {
    await authClient.signOut();
    void navigate("/login", { replace: true });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <section className="pw-card">
        <h1 style={{ margin: "0 0 0.2rem", fontSize: "1.3rem", fontWeight: 600 }}>Konto</h1>
        <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--pw-color-text-muted)" }}>
          {member.displayName} · {email} · Rolle: {member.role === "admin" ? "Administrator" : "Mitglied"}
        </p>
        <button type="button" className="pw-button" onClick={() => void onLogout()} style={{ marginTop: "1rem" }}>
          Abmelden
        </button>
      </section>

      <section className="pw-card">
        <h2 style={{ margin: "0 0 0.8rem", fontSize: "1.05rem", fontWeight: 600 }}>Passwort ändern</h2>
        {message && <div className={`pw-alert pw-alert--${message.kind}`}>{message.text}</div>}
        <form onSubmit={(e) => void onChangePassword(e)} noValidate>
          <div className="pw-field">
            <label htmlFor="currentPassword">Aktuelles Passwort</label>
            <input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="pw-field">
            <label htmlFor="newPassword">Neues Passwort (mind. 12 Zeichen)</label>
            <input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <button type="submit" className="pw-button pw-button--primary" disabled={submitting}>
            {submitting ? "Wird gespeichert …" : "Passwort ändern"}
          </button>
        </form>
      </section>

      <PodcastFeedSection />
    </div>
  );
}
